import { Component, computed, inject } from '@angular/core';
import {
  PmBreadcrumb,
  PmBreadcrumbClickEvent,
  PmBreadcrumbsComponent,
} from '@libs/ui';
import { Store } from '@ngrx/store';
import { AnalysisNavigationService } from 'demand-forecasting/analysis/services/analysis-navigation.service';
import {
  selectAnalysisFilter,
  selectAnalysisHierarchy,
} from 'demand-forecasting/store/router-state/router.selectors';

enum AnalysisBreadcrumbType {
  hierarchy = 'hierarchy',
  filter = 'filter',
}

interface AnalysisHierarchyBreadcrumb extends PmBreadcrumb {
  type: AnalysisBreadcrumbType.hierarchy;
  filter: Record<string, string> | null;
  hierarchy: string[] | null;
}

interface AnalysisFilterBreadcrumb extends PmBreadcrumb {
  type: AnalysisBreadcrumbType.filter;
}
type AnalysisBreadcrumb =
  | AnalysisHierarchyBreadcrumb
  | AnalysisFilterBreadcrumb;

@Component({
  selector: 'pm-analysis-breadcrumbs',
  templateUrl: './analysis-breadcrumbs.component.html',
  imports: [PmBreadcrumbsComponent],
})
export class AnalysisBreadcrumbsComponent {
  private readonly store = inject(Store);
  private readonly hierarchy = this.store.selectSignal(selectAnalysisHierarchy);
  private readonly filter = this.store.selectSignal(selectAnalysisFilter);
  private readonly analysisNavigationService = inject(
    AnalysisNavigationService,
  );

  breadcrumbs = computed<AnalysisBreadcrumb[]>(() => {
    const breadcrumbs: AnalysisBreadcrumb[] = [
      {
        type: AnalysisBreadcrumbType.hierarchy,
        label: 'ANALYSIS.TOTAL',
        clickable: true,
        hierarchy: null,
        filter: null,
      },
    ];

    const hierarchy = this.hierarchy();
    const filter = this.filter();

    hierarchy.forEach((hierarchyLevel, i) => {
      if (i > 0 && i === hierarchy.length - 1) {
        return;
      }
      const onClickHierarchy = hierarchy.slice(0, i + 1);

      const onClickFilter = onClickHierarchy.reduce(
        (acc, level) => {
          if (level === hierarchyLevel) {
            return acc;
          }
          if (filter && filter[level] !== undefined) {
            acc[level] = filter[level];
          }
          return acc;
        },
        {} as Record<string, string>,
      );

      breadcrumbs.push({
        type: AnalysisBreadcrumbType.hierarchy,
        hierarchy: onClickHierarchy,
        filter: onClickFilter,
        label: hierarchyLevel,
        clickable: true,
      });
      if (filter && filter[hierarchyLevel] !== undefined) {
        breadcrumbs.push({
          type: AnalysisBreadcrumbType.filter,
          label: filter[hierarchyLevel] ?? 'ANALYSIS.NOT_APPLICABLE',
        });
      }
    });

    return breadcrumbs;
  });

  protected handleBreadcrumbClick(event: PmBreadcrumbClickEvent): void {
    const breadcrumb = this.breadcrumbs()[event.index];
    if (breadcrumb.type !== AnalysisBreadcrumbType.hierarchy) {
      return;
    }

    this.analysisNavigationService.setQueryParams({
      hierarchy: breadcrumb.hierarchy,
      filter: breadcrumb.filter,
    });
  }
}
